import { ReactElement, useMemo } from "react";
import { useTranslation } from "react-i18next";
import ResonanceLoader from "../../../atoms/ResonanceLoader/ResonanceLoader";
import { getStratificationQuestions } from "../../../../services/user.service";
import { StratQuestionType } from "../../../../types/welcome";
import DisplayStratQuestions from "../../../molecules/Home/Inscription/DisplayStratQuestions";
import Container from "../../../atoms/Container";
import NextPrevButtons from "../../../molecules/NextPrevButtons";
import ScreenTitle from "../../../atoms/ScreenTitle";
import { useFormik } from "formik";
import * as Yup from "yup";
import { useFetch } from "../../../../hooks/useFetch";
import Info from "../../../atoms/ErrorComponent";
import { useConfig } from "../../../../contexts/ConfigContext";
import { useHomeContext } from "../../../../pages/Home/HomeContent";
import Question from "../../../molecules/Question/Question";
import useVocalisation from "../../../../hooks/useVocalisation";
import {
  DateConstraintEnum,
  DateConstraintType,
  DateFormatEnum,
  DateQuestionType,
  QuestionTypeEnum,
} from "../../../../types/Question";
import { ProfileFieldCollectionStateEnum } from "../../../../types/profile";

// Birthdate precision (researcher config) maps to the date-picker DateFormatEnum.
const PRECISION_TO_DATE_FORMAT: Record<string, DateFormatEnum> = {
  YEAR: DateFormatEnum.YEAR,
  MONTH_YEAR: DateFormatEnum.MONTH,
  DAY_MONTH_YEAR: DateFormatEnum.DAY,
};

// No min/max constraint on the birthdate picker (DISABLED -> computeDateLimit returns undefined).
const NO_DATE_LIMIT: DateConstraintType = {
  date_type: DateConstraintEnum.DISABLED,
  todays_date_sens: null,
  todays_date_quantity: null,
  todays_date_unit: null,
  fixed_date: null,
};

// Render the birthdate as a synthetic questionnaire DATE question so it reuses the exact same card,
// title styling and Ionic date picker as the stratification questions on this page.
const buildBirthdateQuestion = (
  format: DateFormatEnum,
  title: string,
  isMandatory: boolean
): DateQuestionType => ({
  id: -1,
  title,
  description: "",
  export_code: "birthdate",
  integral: { is_integral: false, integral_errorcode: null },
  is_mandatory_question: isMandatory,
  is_active: true,
  score_coefficient: 0,
  order: 9999,
  version: "1",
  type: QuestionTypeEnum.DATE,
  detail: { format, min_date: NO_DATE_LIMIT, max_date: NO_DATE_LIMIT },
});

interface StratificationPageProps {
  onNextClick: (obj: any) => void;
}

const StratificationPage = ({
  onNextClick,
}: StratificationPageProps): ReactElement => {
  const { t } = useTranslation();
  const { readText } = useVocalisation();

  const { selectedRole } = useConfig();
  const { welcomeData } = useHomeContext();
  const studyId = welcomeData.id;
  const roleId = selectedRole?.id;

  // Birthdate (age-based profiling) is collected here, alongside the list-attribute questions.
  const birthdateField = selectedRole?.access?.fields?.find((field) => field.key === "birthdate");
  const collectsBirthdate =
    !!birthdateField && birthdateField.collectionState !== ProfileFieldCollectionStateEnum.HIDDEN;
  const birthdateRequired =
    birthdateField?.collectionState === ProfileFieldCollectionStateEnum.REQUIRED;
  const birthdateFormat =
    PRECISION_TO_DATE_FORMAT[
      birthdateField?.config?.precision ?? selectedRole?.birthdate_precision ?? "MONTH_YEAR"
    ] ?? DateFormatEnum.MONTH;

  const {
    isError,
    isLoading,
    data: questions,
  } = useFetch<StratQuestionType[]>(
    "stratifications",
    getStratificationQuestions,
    { studyId, roleId }
  );

  const postAnswers = (values) => {
    const obj = questions.map(({ id, version, answers }) => ({
      question_id: id,
      question_version: version,
      answer_id: values[id],
      answer_version: answers.find((answer) => answer.id === Number(values[id]))
        .version,
    }));
    // Pass the list-attribute answers AND the birthdate up; HomeContent merges birthdate into the user.
    onNextClick({ stratification: obj, birthdate: values.birthdate || undefined });
  };

  const validationSchema = useMemo(
    function () {
      const obj = {};
      questions?.forEach(({ id = 0 }) => {
        obj[id] = Yup.string().required();
      });
      if (collectsBirthdate && birthdateRequired) {
        obj["birthdate"] = Yup.string().required();
      }

      return Yup.object().shape(obj);
    },
    [questions, collectsBirthdate, birthdateRequired]
  );

  const initialValues = useMemo(
    function () {
      const obj = {};

      questions?.forEach(({ id = 0 }) => {
        obj[id] = "";
      });
      obj["birthdate"] = "";

      return obj;
    },
    [questions]
  );

  const { values, touched, errors, submitForm, setFieldValue } = useFormik({
    initialValues,
    onSubmit: postAnswers,
    validationSchema: validationSchema,
    enableReinitialize: true,
  });

  if (isLoading) return <ResonanceLoader />;

  if (isError)
    return (
      <Info
        title={t("common_server_error_title")}
        description={t("common_server_error_desc")}
        actionText={t("common_go_back")}
      />
    );

  return (
    <Container>
      <ScreenTitle>{t("home_stratification_title")}</ScreenTitle>

      <DisplayStratQuestions
        stratQuestions={questions}
        values={values}
        setFieldValue={(key, value) => setFieldValue(key, value)}
        touched={touched}
        errors={errors}
      />

      {collectsBirthdate && (
        <Question
          question={buildBirthdateQuestion(
            birthdateFormat,
            t("home_inscription_birthdate"),
            birthdateRequired
          )}
          answer={values.birthdate ?? ""}
          onAnswerChange={(value) => setFieldValue("birthdate", (value as string) ?? "")}
          ruleMessage={
            touched.birthdate && errors.birthdate ? t("form_error_required") : undefined
          }
          readText={readText}
          cardMargin="12px 20px"
        />
      )}

      <NextPrevButtons onNextClick={submitForm} />
    </Container>
  );
};

export default StratificationPage;
